/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FileText, Receipt, Download, CheckCircle2, Clock, Loader2, FileSignature, CreditCard, Building2, Copy, X, Landmark, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { documentsApi, formatApiError } from "@/lib/api";
import { formatDateLong, formatDateTimeLong } from "@/lib/date";

const STATUS_LABEL = {
  brouillon: "Brouillon",
  envoye: "À signer",
  envoyee: "Envoyée",
  accepte: "Accepté",
  signe: "Signé",
  acompte_recu: "Acompte reçu",
  refuse: "Refusé",
  expire: "Expiré",
  payee: "Payée",
  partielle: "Partiellement payée",
  impayee: "Impayée",
  annulee: "Annulée",
};

function fmtEur(v) {
  return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(iso) {
  return iso ? (formatDateLong(iso) || "—") : "—";
}

export default function MesDocuments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    documentsApi.list()
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const VALIDATED_STATUSES = new Set(["accepte", "signe", "acompte_recu", "payee", "partielle"]);
  const hasValidatedDoc = useMemo(
    () => items.some((d) => VALIDATED_STATUSES.has(d.status) || d.signed_at),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const openPdf = async (id) => {
    const token = localStorage.getItem("ma_token");
    try {
      const r = await fetch(documentsApi.pdfUrl(id), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("PDF indisponible");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Impossible de récupérer le PDF.");
    }
  };

  const devis = items.filter((d) => d.doc_type === "devis");
  const factures = items.filter((d) => d.doc_type === "facture" || d.doc_type === "facture_acompte");

  return (
    <div data-testid="ec-documents">
      <header className="mb-10">
        <p className="eyebrow">Espace client</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          Mes <span className="italic gold-text">documents.</span>
        </h1>
        <p className="text-white/60 text-sm md:text-base mt-3 max-w-2xl">
          Retrouvez ici l'ensemble de vos devis, contrats signés et factures. Téléchargez-les à tout moment au format PDF.
        </p>
      </header>

      {loading ? (
        <p className="text-white/55 italic flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : items.length === 0 ? (
        <div className="card-luxe p-12 text-center">
          <FileText size={36} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
          <p className="text-white/65 mb-2">Aucun document pour le moment.</p>
          <p className="text-white/45 text-sm">Dès qu'un devis ou une facture sera émis(e), vous le retrouverez ici.</p>
          <Link to="/contact" className="btn-ghost-gold mt-6 inline-flex">Demander un devis</Link>
        </div>
      ) : (
        <div className="space-y-10">
          {hasValidatedDoc && <InsuranceAttestationCard />}
          {devis.length > 0 && (
            <Section title="Devis & contrats" icon={FileText} items={devis} openPdf={openPdf} />
          )}
          {factures.length > 0 && (
            <Section title="Factures" icon={Receipt} items={factures} openPdf={openPdf} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * RC Pro insurance attestation banner — shown once the client has at least one
 * validated devis (accepté, signé, acompte reçu) or a paid facture.
 */
function InsuranceAttestationCard() {
  return (
    <div
      className="card-luxe p-5 md:p-6 border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.03] to-transparent"
      data-testid="insurance-attestation-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/10 border border-emerald-400/30 shrink-0">
          <ShieldCheck size={20} strokeWidth={1.5} className="text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/85 mb-1">
            Sérénité incluse
          </p>
          <h3 className="font-serif-luxe text-xl md:text-2xl">
            Attestation <span className="italic gold-text">RC Pro.</span>
          </h3>
          <p className="text-white/65 text-sm mt-2 leading-relaxed">
            My Animation est couvert par une assurance Responsabilité Civile
            Professionnelle. Téléchargez l'attestation officielle à joindre à
            vos éventuelles demandes de justificatifs auprès de la salle,
            mairie ou traiteur.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]">
            <span className="text-white/50">
              N° police : <span className="text-white/85 font-mono">RCP250814180127</span>
            </span>
            <span className="text-white/50">
              Assureur : <span className="text-white/85">Assurup</span>
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="/documents/attestation-rc-pro.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              className="btn-gold text-sm inline-flex items-center gap-2"
              data-testid="download-insurance-attestation"
            >
              <Download size={14} strokeWidth={1.6} />
              Télécharger l'attestation
            </a>
            <a
              href="/documents/attestation-rc-pro.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-gold text-sm inline-flex items-center gap-2"
              data-testid="preview-insurance-attestation"
            >
              <FileText size={14} strokeWidth={1.6} />
              Aperçu PDF
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, items, openPdf }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-5">
        <Icon size={16} strokeWidth={1.4} className="text-[#DCB457]" />
        <h2 className="font-serif-luxe text-xl md:text-2xl">{title}</h2>
        <span className="text-[11px] uppercase tracking-[0.18em] text-white/40">({items.length})</span>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((d) => <DocCard key={d.id} d={d} openPdf={openPdf} />)}
      </ul>
    </section>
  );
}

function DocCard({ d, openPdf }) {
  const signed = !!d.is_signed;
  const statusLabel = STATUS_LABEL[d.status] || d.status;
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [transferInfo, setTransferInfo] = useState(null);

  // Determine highlighted next action for the client
  const showSignBtn = d.doc_type === "devis" && !signed && d.signature_token && !["brouillon", "refuse"].includes(d.status);
  // Show acompte-pay button only for signed devis that don't yet have an acompte invoice
  const showDepositBtn = d.doc_type === "devis" && signed && !d.acompte_invoice_id && Number(d.deposit_ttc || 0) > 0;
  const transferAwaiting = d.doc_type === "devis" && d.acompte_transfer_requested_at && !d.acompte_invoice_id;

  const payByCard = async () => {
    setPayingDeposit(true);
    try {
      const { checkout_url } = await documentsApi.createDepositCheckout(d.id, window.location.origin);
      if (checkout_url) window.location.href = checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible de démarrer le paiement en ligne.";
      toast.error(msg);
      setPayingDeposit(false);
    }
  };

  const payByTransfer = async () => {
    setPayingDeposit(true);
    try {
      const info = await documentsApi.requestDepositTransfer(d.id);
      setTransferInfo(info);
      setShowPayModal(false);
      toast.success("Coordonnées bancaires envoyées par email.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible de traiter la demande.";
      toast.error(msg);
    } finally { setPayingDeposit(false); }
  };

  return (
    <li className="card-luxe p-5 relative overflow-hidden group" data-testid={`doc-card-${d.number}`}>
      {signed && (
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-emerald-500/10 blur-2xl" />
      )}
      <div className="relative flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-mono text-xs text-[#DCB457]">{d.number}</p>
          {d.is_deposit_invoice && (
            <p className="text-[9px] uppercase tracking-[0.18em] text-emerald-300 mt-0.5">Facture d'acompte</p>
          )}
          <p className="font-serif-luxe text-lg mt-1">
            {d.event_type}
            {d.event_date && <span className="text-white/55"> · {fmtDate(d.event_date)}</span>}
          </p>
          {d.event_location && <p className="text-xs text-white/45 mt-0.5">{d.event_location}</p>}
        </div>
        <span className={`text-[10px] uppercase tracking-[0.16em] border px-2 py-0.5 whitespace-nowrap ${
          signed ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/8"
          : ["payee", "accepte"].includes(d.status) ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/8"
          : ["envoye", "envoyee"].includes(d.status) ? "text-[#DCB457] border-[#DCB457]/40 bg-[#DCB457]/8"
          : "text-white/55 border-white/15 bg-white/3"
        }`}>
          {signed ? "Signé" : statusLabel}
        </span>
      </div>

      <div className="relative flex items-center justify-between text-sm mb-4">
        <span className="text-white/55">Total TTC</span>
        <span className="font-serif-luxe text-xl gold-text">{fmtEur(d.total_ttc)}</span>
      </div>

      {d.doc_type === "devis" && (
        <div className="relative flex items-center justify-between text-xs text-white/55 pb-3 border-b border-[#DCB457]/10 mb-3">
          <span>Acompte {d.deposit_pct}%</span>
          <span className="text-white/75">{fmtEur(d.deposit_ttc)}</span>
        </div>
      )}

      {signed && d.signed_at && (
        <p className="relative text-[11px] text-emerald-300/85 flex items-center gap-1.5 mb-3">
          <CheckCircle2 size={12} strokeWidth={1.5} />
          Signé le {formatDateTimeLong(d.signed_at)}
        </p>
      )}
      {!signed && d.doc_type === "devis" && (
        <p className="relative text-[11px] text-white/55 flex items-center gap-1.5 mb-3">
          <Clock size={12} strokeWidth={1.5} /> En attente de signature
        </p>
      )}

      <div className="relative flex items-center gap-2 flex-wrap">
        <button
          onClick={() => openPdf(d.id)}
          data-testid={`doc-pdf-${d.number}`}
          className="text-[11px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/40 px-3 py-1.5 hover:bg-[#DCB457]/10 inline-flex items-center gap-1.5"
        >
          <Download size={11} strokeWidth={1.5} /> Télécharger PDF
        </button>
        {showSignBtn && (
          <Link
            to={`/signature/${d.signature_token}`}
            data-testid={`doc-sign-${d.number}`}
            className="text-[11px] uppercase tracking-[0.18em] bg-[#DCB457] text-black px-3 py-1.5 hover:bg-[#E5C24A] inline-flex items-center gap-1.5"
          >
            <FileSignature size={11} strokeWidth={1.5} /> Signer ce devis
          </Link>
        )}
        {showDepositBtn && (
          <button
            onClick={() => setShowPayModal(true)}
            disabled={payingDeposit}
            data-testid={`doc-deposit-${d.number}`}
            className="text-[11px] uppercase tracking-[0.18em] bg-[#DCB457] text-black px-3 py-1.5 hover:bg-[#E5C24A] inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {payingDeposit ? (
              <><Loader2 size={11} className="animate-spin" /> Redirection…</>
            ) : (
              <><CreditCard size={11} strokeWidth={1.5} /> Payer l'acompte</>
            )}
          </button>
        )}
        {transferAwaiting && (
          <span className="text-[11px] uppercase tracking-[0.18em] text-sky-300 border border-sky-400/40 bg-sky-500/8 px-3 py-1.5 inline-flex items-center gap-1.5">
            <Clock size={11} strokeWidth={1.5} /> Virement en attente
          </span>
        )}
        {d.acompte_invoice_number && (
          <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-400/40 bg-emerald-500/8 px-3 py-1.5 inline-flex items-center gap-1.5">
            <CheckCircle2 size={11} strokeWidth={1.5} /> Acompte reçu · {d.acompte_invoice_number}
          </span>
        )}
      </div>

      {/* Payment method choice modal */}
      {showPayModal && (
        <PaymentMethodModal
          doc={d}
          onCard={payByCard}
          onTransfer={payByTransfer}
          onClose={() => setShowPayModal(false)}
          busy={payingDeposit}
        />
      )}

      {/* Transfer confirmation modal (after client picks transfer) */}
      {transferInfo && (
        <TransferInstructionsModal
          info={transferInfo}
          onClose={() => setTransferInfo(null)}
        />
      )}
    </li>
  );
}


// ==========================================================================
// Payment method chooser (card vs transfer)
// ==========================================================================
function PaymentMethodModal({ doc, onCard, onTransfer, onClose, busy }) {
  const amount = Number(doc.deposit_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 });

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="deposit-method-modal"
      style={{ isolation: "isolate" }}
    >
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative w-full max-w-lg bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-6 pb-4 border-b border-[#DCB457]/15">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-[#DCB457]" aria-label="Fermer" data-testid="deposit-method-close">
            <X size={18} strokeWidth={1.4} />
          </button>
          <p className="eyebrow">Acompte {doc.deposit_pct || 30}%</p>
          <h3 className="font-serif-luxe text-2xl mt-1">
            Comment souhaitez-vous <span className="italic gold-text">régler ?</span>
          </h3>
          <p className="text-white/60 text-sm mt-2">
            Montant : <strong className="text-[#DCB457]">{amount} €</strong> — devis {doc.number}
          </p>
        </div>

        <div className="p-6 space-y-3">
          <button
            type="button"
            onClick={onCard}
            disabled={busy}
            data-testid="deposit-method-card"
            className="w-full flex items-start gap-4 p-4 border border-[#DCB457]/30 hover:border-[#DCB457] hover:bg-[#DCB457]/[0.06] transition-colors duration-150 text-left disabled:opacity-60"
          >
            <div className="w-11 h-11 flex items-center justify-center bg-[#DCB457]/10 border border-[#DCB457]/30 shrink-0">
              <CreditCard size={18} strokeWidth={1.4} className="text-[#DCB457]" />
            </div>
            <div className="flex-1">
              <p className="font-serif-luxe text-lg">Carte bancaire</p>
              <p className="text-white/55 text-sm mt-0.5">
                Paiement immédiat et sécurisé (Stripe). Votre date est réservée instantanément.
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={onTransfer}
            disabled={busy}
            data-testid="deposit-method-transfer"
            className="w-full flex items-start gap-4 p-4 border border-white/15 hover:border-[#DCB457] hover:bg-[#DCB457]/[0.06] transition-colors duration-150 text-left disabled:opacity-60"
          >
            <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/15 shrink-0">
              <Landmark size={18} strokeWidth={1.4} className="text-white/85" />
            </div>
            <div className="flex-1">
              <p className="font-serif-luxe text-lg">Virement bancaire</p>
              <p className="text-white/55 text-sm mt-0.5">
                Nous vous envoyons nos coordonnées bancaires par email. Réservation confirmée à réception.
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ==========================================================================
// Transfer instructions display (after client picks transfer)
// ==========================================================================
function TransferInstructionsModal({ info, onClose }) {
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié.`);
  };
  const amount = Number(info.amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 });

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="transfer-modal"
      style={{ isolation: "isolate" }}
    >
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative w-full max-w-lg bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-6 pb-4 border-b border-[#DCB457]/15">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-[#DCB457]" aria-label="Fermer" data-testid="transfer-modal-close">
            <X size={18} strokeWidth={1.4} />
          </button>
          <p className="eyebrow">Coordonnées bancaires</p>
          <h3 className="font-serif-luxe text-2xl mt-1">
            Instructions de <span className="italic gold-text">virement.</span>
          </h3>
          <p className="text-white/60 text-sm mt-2">
            Vous recevez également ces informations par email.
          </p>
        </div>
        <div className="p-6 space-y-3">
          <FieldRow label="Montant" value={`${amount} €`} onCopy={() => copy(String(info.amount), "Montant")} highlight />
          <FieldRow label="Référence à indiquer" value={info.reference} onCopy={() => copy(info.reference, "Référence")} highlight />
          <div className="border-t border-[#DCB457]/15 pt-3" />
          <FieldRow label="Bénéficiaire" value={info.titulaire} onCopy={() => copy(info.titulaire, "Bénéficiaire")} />
          <FieldRow label="Banque" value={info.bank} onCopy={() => copy(info.bank, "Banque")} />
          <FieldRow label="IBAN" value={info.iban} onCopy={() => copy((info.iban || "").replace(/\s/g, ""), "IBAN")} mono />
          <FieldRow label="BIC" value={info.bic} onCopy={() => copy(info.bic, "BIC")} mono />

          <p className="text-xs text-white/50 leading-relaxed pt-3">
            <Building2 size={11} strokeWidth={1.5} className="inline mr-1 -mt-0.5 text-[#DCB457]" />
            Une fois le virement effectué, votre facture d'acompte sera émise dès réception (2-5 jours ouvrés).
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function FieldRow({ label, value, onCopy, mono, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/50 shrink-0">{label}</p>
      <div className="flex items-center gap-2 min-w-0">
        <p className={`text-sm ${mono ? "font-mono" : ""} ${highlight ? "text-[#DCB457] font-bold" : "text-white/90"} truncate`}>
          {value}
        </p>
        <button
          type="button"
          onClick={onCopy}
          className="text-white/40 hover:text-[#DCB457] p-1"
          aria-label={`Copier ${label}`}
        >
          <Copy size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
